import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Crown, Cake, Calendar, DollarSign, Edit2, Check, X, Plus, Gift as GiftIcon, ChevronDown, ChevronUp, Trash2, History } from 'lucide-react';
import { toast } from 'sonner';

const BirthdayTracker = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingBudget, setEditingBudget] = useState(null);
  const [tempBudget, setTempBudget] = useState('');
  const [showAddGiftDialog, setShowAddGiftDialog] = useState(null);
  const [newGift, setNewGift] = useState({ gift_name: '', price: 0, notes: '', image_url: '' });
  const [expandedRecipient, setExpandedRecipient] = useState(null);
  const [importUrl, setImportUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [recipientGifts, setRecipientGifts] = useState({});
  const [showHistoryDialog, setShowHistoryDialog] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (user?.subscription_tier !== 'elite') {
      toast.error('Elite subscription required');
      navigate('/subscription');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const response = await api.get('/birthday-tracker');
      setData(response.data);
      
      // Fetch gifts for each recipient
      const giftsData = {};
      for (const recipient of response.data.recipients || []) {
        if (recipient.birthday) {
          try {
            const giftsResponse = await api.get(`/birthday-tracker/gifts/${recipient.id}`);
            giftsData[recipient.id] = giftsResponse.data;
          } catch (error) {
            console.error(`Failed to fetch gifts for recipient ${recipient.id}:`, error);
            giftsData[recipient.id] = [];
          }
        }
      }
      setRecipientGifts(giftsData);
    } catch (error) {
      if (error.response?.status === 403) {
        navigate('/subscription');
      } else {
        toast.error('Failed to load birthday tracker');
      }
    } finally {
      setLoading(false);
    }
  };

  const startEditingBudget = (recipient) => {
    setEditingBudget(recipient.id);
    setTempBudget(recipient.birthday_budget || 0);
  };

  const cancelEditingBudget = () => {
    setEditingBudget(null);
    setTempBudget('');
  };

  const saveBudget = async (recipientId) => {
    try {
      await api.patch(`/recipients/${recipientId}/birthday-budget?budget=${parseFloat(tempBudget) || 0}`);
      toast.success('Budget updated!');
      setEditingBudget(null);
      setTempBudget('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update budget');
    }
  };

  const openAddGiftDialog = (recipient) => {
    setShowAddGiftDialog(recipient);
    setNewGift({ gift_name: '', price: 0, notes: '', image_url: '' });
    setImportUrl('');
  };

  const importFromUrl = async () => {
    if (!importUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    try {
      setExtracting(true);
      const response = await api.post(`/gifts/extract-from-url?url=${encodeURIComponent(importUrl)}`);
      const extractedData = response.data;
      
      // Pre-fill the gift form with extracted data
      setNewGift({
        gift_name: extractedData.gift_name || '',
        price: extractedData.price || 0,
        notes: extractedData.store_name ? `From ${extractedData.store_name}` : '',
        image_url: extractedData.image_url || ''
      });
      
      setImportUrl('');
      toast.success('Gift details extracted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract gift details');
    } finally {
      setExtracting(false);
    }
  };

  const addBirthdayGift = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/birthday-tracker/gifts', null, {
        params: {
          recipient_id: showAddGiftDialog.id,
          gift_name: newGift.gift_name,
          price: parseFloat(newGift.price) || 0,
          image_url: newGift.image_url,
          notes: newGift.notes
        }
      });
      toast.success('Birthday gift added!');
      setShowAddGiftDialog(null);
      setNewGift({ gift_name: '', price: 0, notes: '', image_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add gift');
    }
  };

  const deleteBirthdayGift = async (giftId) => {
    try {
      await api.delete(`/birthday-tracker/gifts/${giftId}`);
      toast.success('Gift deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete gift');
    }
  };

  const toggleGiftPurchased = async (giftId) => {
    try {
      await api.patch(`/birthday-tracker/gifts/${giftId}/purchased`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update gift');
    }
  };

  const viewHistory = async (recipient) => {
    setShowHistoryDialog(recipient);
    setLoadingHistory(true);
    try {
      const response = await api.get(`/birthday-tracker/history/${recipient.id}`);
      setHistoryData(response.data);
    } catch (error) {
      toast.error('Failed to load history');
      setShowHistoryDialog(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  const getNextBirthday = (birthday) => {
    if (!birthday) return null;
    const bday = new Date(birthday);
    const today = new Date();
    const currentYear = today.getFullYear();
    let nextBday = new Date(currentYear, bday.getMonth(), bday.getDate());
    
    if (nextBday < today) {
      nextBday = new Date(currentYear + 1, bday.getMonth(), bday.getDate());
    }
    return nextBday;
  };

  const getDaysUntil = (birthday) => {
    const next = getNextBirthday(birthday);
    if (!next) return null;
    const today = new Date();
    const diff = Math.ceil((next - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const formatBirthday = (birthday) => {
    if (!birthday) return 'Not set';
    const date = new Date(birthday);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  const recipientsWithBirthdays = data?.recipients.filter(r => r.birthday) || [];
  const recipientsWithoutBirthdays = data?.recipients.filter(r => !r.birthday) || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-green-50" data-testid="birthday-tracker-page">
      <header className="bg-white shadow-sm border-b border-red-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Cake className="w-8 h-8 text-red-600" />
            <h1 className="text-4xl font-bold gradient-text">Birthday Tracker {data?.current_year || new Date().getFullYear()}</h1>
            <Crown className="w-6 h-6 text-amber-600" />
          </div>
          <p className="text-gray-600">Track birthday budgets and spending for all your recipients (resets annually)</p>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
                Total Budget
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(data?.total_budget || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">across all birthdays</p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="w-5 h-5 text-red-600" />
                Total Spent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">
                {formatCurrency(data?.total_spent || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">on birthday gifts</p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="w-5 h-5 text-blue-600" />
                Remaining
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">
                {formatCurrency(data?.budget_remaining || 0)}
              </p>
              <p className="text-sm text-gray-600 mt-1">budget left</p>
            </CardContent>
          </Card>
        </div>

        {/* Recipients with Birthdays */}
        {recipientsWithBirthdays.length > 0 && (
          <Card className="glass-card mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-red-600" />
                Upcoming Birthdays
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recipientsWithBirthdays.map((recipient) => {
                  const daysUntil = getDaysUntil(recipient.birthday);
                  const isEditing = editingBudget === recipient.id;
                  
                  return (
                    <div
                      key={recipient.id}
                      className="flex items-center justify-between p-4 bg-white rounded-lg border hover:border-red-200 transition-colors"
                      data-testid={`recipient-${recipient.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{recipient.name}</h3>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => viewHistory(recipient)}
                            className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-6 px-2"
                            data-testid={`view-history-${recipient.id}`}
                          >
                            <History className="w-3 h-3 mr-1" />
                            View History
                          </Button>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Cake className="w-4 h-4" />
                            {formatBirthday(recipient.birthday)}
                          </span>
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => viewHistory(recipient)}
                            className="text-xs text-blue-600 hover:text-blue-700 p-0 h-auto"
                          >
                            <History className="w-3 h-3 mr-1" />
                            History
                          </Button>
                          {daysUntil !== null && (
                            <span className={`font-medium ${daysUntil <= 7 ? 'text-red-600' : 'text-gray-700'}`}>
                              {daysUntil === 0 ? '🎉 Today!' : `in ${daysUntil} days`}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAddGiftDialog(recipient)}
                          className="border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Gift
                        </Button>
                        
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Budget</p>
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={tempBudget}
                                onChange={(e) => setTempBudget(e.target.value)}
                                className="w-24 h-8"
                                autoFocus
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => saveBudget(recipient.id)}
                                className="p-1 h-8 w-8"
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEditingBudget}
                                className="p-1 h-8 w-8"
                              >
                                <X className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <p className="text-lg font-bold text-green-600">
                                {formatCurrency(recipient.birthday_budget || 0)}
                              </p>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => startEditingBudget(recipient)}
                                className="p-1 h-8 w-8"
                              >
                                <Edit2 className="w-4 h-4 text-gray-400" />
                              </Button>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Spent</p>
                          <p className="text-lg font-bold text-red-600">
                            {formatCurrency(recipient.birthday_spent || 0)}
                          </p>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-32">
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>
                              {recipient.birthday_budget > 0 
                                ? Math.round((recipient.birthday_spent / recipient.birthday_budget) * 100)
                                : 0}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                recipient.birthday_budget > 0
                                  ? (recipient.birthday_spent / recipient.birthday_budget) >= 1
                                    ? 'bg-red-500'
                                    : (recipient.birthday_spent / recipient.birthday_budget) >= 0.8
                                    ? 'bg-yellow-500'
                                    : 'bg-green-500'
                                  : 'bg-gray-300'
                              }`}
                              style={{
                                width: `${Math.min(
                                  recipient.birthday_budget > 0
                                    ? (recipient.birthday_spent / recipient.birthday_budget) * 100
                                    : 0,
                                  100
                                )}%`
                              }}
                            />
                          </div>
                        </div>
                        
                        {/* Toggle Gifts Button */}
                        {recipientGifts[recipient.id]?.length > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpandedRecipient(
                              expandedRecipient === recipient.id ? null : recipient.id
                            )}
                            className="p-2"
                            data-testid={`toggle-gifts-${recipient.id}`}
                          >
                            {expandedRecipient === recipient.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                      
                      {/* Expanded Gifts List */}
                      {expandedRecipient === recipient.id && recipientGifts[recipient.id]?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
                            <GiftIcon className="w-4 h-4" />
                            Birthday Gifts ({recipientGifts[recipient.id].length})
                          </h4>
                          <div className="space-y-2">
                            {recipientGifts[recipient.id].map((gift) => (
                              <div
                                key={gift.id}
                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                                data-testid={`gift-${gift.id}`}
                              >
                                {gift.image_url && (
                                  <img
                                    src={gift.image_url}
                                    alt={gift.gift_name}
                                    className="w-12 h-12 object-cover rounded"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{gift.gift_name}</p>
                                  <p className="text-sm text-gray-600">
                                    {formatCurrency(gift.price)}
                                    {gift.notes && ` • ${gift.notes}`}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={gift.is_purchased}
                                    onCheckedChange={() => toggleGiftPurchased(gift.id)}
                                    data-testid={`gift-purchased-${gift.id}`}
                                  />
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteBirthdayGift(gift.id)}
                                    className="p-1 h-8 w-8 text-red-600 hover:bg-red-50"
                                    data-testid={`delete-gift-${gift.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recipients without Birthdays */}
        {recipientsWithoutBirthdays.length > 0 && (
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-gray-600">Recipients without Birthdays</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Add birthdays to these recipients to track their birthday budgets.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {recipientsWithoutBirthdays.map((recipient) => (
                  <div
                    key={recipient.id}
                    className="p-3 bg-gray-50 rounded border border-gray-200"
                  >
                    <p className="font-medium">{recipient.name}</p>
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => navigate('/recipients')}
                      className="p-0 h-auto text-xs text-blue-600"
                    >
                      Add birthday →
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {recipientsWithBirthdays.length === 0 && recipientsWithoutBirthdays.length === 0 && (
          <Card className="glass-card">
            <CardContent className="p-12 text-center">
              <Cake className="w-16 h-16 text-red-600 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No Recipients Yet</h3>
              <p className="text-gray-600 mb-4">
                Add recipients with birthdays to start tracking birthday budgets.
              </p>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={() => navigate('/recipients')}
              >
                Go to Recipients
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Add Birthday Gift Dialog */}
      <Dialog open={!!showAddGiftDialog} onOpenChange={(open) => !open && setShowAddGiftDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>🎂 Add Birthday Gift for {showAddGiftDialog?.name}</DialogTitle>
          </DialogHeader>
          
          {/* URL Import Section */}
          <div className="border-b pb-4 mb-4">
            <label className="text-sm font-medium mb-2 block">Import from URL (Optional)</label>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://www.amazon.com/product..."
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                className="flex-1"
              />
              <Button
                type="button"
                onClick={importFromUrl}
                disabled={extracting || !importUrl}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                {extracting ? 'Extracting...' : 'Extract'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Paste a product URL to automatically fill gift details
            </p>
          </div>

          <form onSubmit={addBirthdayGift} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Gift Name *</label>
              <Input
                placeholder="e.g., Watch, Book, etc."
                value={newGift.gift_name}
                onChange={(e) => setNewGift({ ...newGift, gift_name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Price *</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={newGift.price}
                onChange={(e) => setNewGift({ ...newGift, price: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Image URL</label>
              <Input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={newGift.image_url}
                onChange={(e) => setNewGift({ ...newGift, image_url: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Notes</label>
              <Textarea
                placeholder="Optional notes"
                value={newGift.notes}
                onChange={(e) => setNewGift({ ...newGift, notes: e.target.value })}
                rows={2}
              />
            </div>
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">
              Add Birthday Gift
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!showHistoryDialog} onOpenChange={() => setShowHistoryDialog(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Birthday Gift History - {showHistoryDialog?.name}
            </DialogTitle>
          </DialogHeader>

          {loadingHistory ? (
            <div className="py-8 text-center text-gray-500">Loading history...</div>
          ) : historyData?.history?.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-gray-500">No historical data available yet.</p>
              <p className="text-sm text-gray-400 mt-2">
                Past birthday gifts will appear here starting next year.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Recipient Info */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Recipient</p>
                      <p className="font-semibold text-lg">{historyData?.recipient?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Birthday</p>
                      <p className="font-semibold text-lg">{formatBirthday(historyData?.recipient?.birthday)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Annual Budget</p>
                      <p className="font-semibold text-lg text-green-600">
                        {formatCurrency(historyData?.recipient?.birthday_budget || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Current Year</p>
                      <p className="font-semibold text-lg text-blue-600">{data?.current_year || new Date().getFullYear()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Historical Years */}
              {historyData?.history?.map((yearData) => (
                <Card key={yearData.year} className="border-2">
                  <CardHeader className="bg-gray-50">
                    <CardTitle className="flex items-center justify-between">
                      <span className="text-xl font-bold">{yearData.year}</span>
                      <div className="flex items-center gap-6 text-sm font-normal">
                        <span className="text-gray-600">
                          {yearData.purchased_count} of {yearData.total_gifts} gifts purchased
                        </span>
                        <span className="font-bold text-red-600">
                          Spent: {formatCurrency(yearData.total_spent)}
                        </span>
                        <span className="text-gray-600">
                          Budget: {formatCurrency(yearData.budget)}
                        </span>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {yearData.gifts.length > 0 ? (
                      <div className="space-y-2">
                        {yearData.gifts.map((gift) => (
                          <div
                            key={gift.id}
                            className={`flex items-center gap-3 p-3 rounded-lg ${
                              gift.is_purchased ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                            }`}
                          >
                            {gift.image_url && (
                              <img
                                src={gift.image_url}
                                alt={gift.gift_name}
                                className="w-12 h-12 object-cover rounded"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div className="flex-1">
                              <p className="font-medium">
                                {gift.gift_name}
                                {gift.is_purchased && (
                                  <span className="ml-2 text-xs text-green-600 font-semibold">✓ PURCHASED</span>
                                )}
                              </p>
                              <p className="text-sm text-gray-600">
                                {formatCurrency(gift.price)}
                                {gift.notes && ` • ${gift.notes}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No gifts recorded for this year</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BirthdayTracker;
